import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ScrollableDataTableHead from './ScrollableDataTableHead';
import ScrollableDataTableBody from './ScrollableDataTableBody';
// @ts-ignore
import styles from './ScrollableDataTable.module.scss';
import { ITableColumn } from '../../../../Models/Table/ITableColumn';

export interface IScrollableDataTable<T> {
	columns: ITableColumn[];
	rows: T[];
	firstColumnFixed?: boolean;
	onRowClick?: (row: T) => void;
}

const ScrollableDataTable = <T,>({
	columns,
	rows,
	firstColumnFixed = true,
	onRowClick,
}: IScrollableDataTable<T>): React.ReactElement => {
	const tableRef = useRef<any>();
	const [topScrollerWidth, setTopScrollerWidth] = useState<number>(0);
	const topScrollRef = useRef<any>(null);
	const contentScrollRef = useRef<any>(null);

	useLayoutEffect(() => {
		if (tableRef?.current) {
			setTopScrollerWidth(tableRef.current.clientWidth);
			contentScrollRef.current.scroll({
				left: tableRef.current.clientWidth,
				behavior: 'smooth',
			});
		}
	}, [tableRef]);

	useEffect(() => {
		const topScroll = topScrollRef.current;
		const contentScroll = contentScrollRef.current;

		if (topScroll && contentScroll) {
			const handleScroll = (source: any, target: any) => {
				const sync = () => {
					target.scrollLeft = source.scrollLeft;
				};
				source.addEventListener('scroll', sync);
				return () => source.removeEventListener('scroll', sync);
			};

			const cleanUpTop = handleScroll(topScroll, contentScroll);
			const cleanUpContent = handleScroll(contentScroll, topScroll);

			return () => {
				cleanUpTop();
				cleanUpContent();
			};
		}
	}, []);

	return (
		<div className={styles.wrapper}>
			<div className={styles.scrollContainer}>
				<div className={styles.topScroller} ref={topScrollRef}>
					<div style={{ width: topScrollerWidth }}>&nbsp;</div>
				</div>
				<div className={styles.scrollWrapper} ref={contentScrollRef}>
					<table className={styles.table} ref={tableRef}>
						<ScrollableDataTableHead
							columns={columns}
							firstColumnFixed={firstColumnFixed}
						/>
						<ScrollableDataTableBody
							rows={rows}
							columns={columns}
							firstColumnFixed={firstColumnFixed}
							onRowClick={onRowClick}
						/>
					</table>
				</div>
				<div className={styles.bottomScroller} />
			</div>
		</div>
	);
};

export default ScrollableDataTable;
